import './selectMachineType.css'

import React, { useEffect, useMemo, useState } from "react"
import { Services } from "@/services/services"
import { CurrentSession } from "@/services/repository/session"
import { useTranslations } from "@/hooks/useTranslations"
import { MachineType } from "@/services/model/machine"
import { useModal } from '@/components/modal/Modal'
import { InsertMachineTypePopup } from './insertMachineType'
import { MachineTypeFormData } from '@/components/forms/machinetypes/MachineType'
import { Roles } from '@/services/repository/users'
import { useAppContext } from '@/context/app'

export interface Props {
  onSelect: (machineType: MachineType) => void
  onClose: () => void
}

export const MachineTypeSelectPopup: React.FC<Props> = ({
  onClose, onSelect
}) => {
  const { services, session, translates } = useAppContext()
  const [machineTypes, setMachineTypes] = useState<MachineType[] | undefined>()
  const [selectedManufacturer, setSelectedManufacturer] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  const { openModal } = useModal()

  useEffect(() => {
    services.machines.types().then(setMachineTypes)
  }, [])

  function addType() {
    openModal((props) => InsertMachineTypePopup({
      ...props, services, session, onCreate: (data: MachineType) => {
        onClose()
        onSelect(data)
      }
    }))
  }

  const isManager = useMemo(
    () => session ? [Roles.SuperAdmin, Roles.CompanyAdmin, Roles.CompanyManager].includes(session.user.role as any) : false,
    [session]
  );


  if (!machineTypes || !session) {
    return <div className="spinner"></div>
  }

  // Step 1: Select Manufacturer
  if (!selectedManufacturer) {
    const manufacturers = Array.from(new Set(machineTypes.map(mt => mt.manufacturer))).sort()
    return <div className="machine-type-select-popup">
      {isManager ? <div className='left-btns'>
        <button className="popup-add" onClick={() => addType()}>
          {translates?.for('popup.machine_type_select.add')}
        </button>
      </div> : null}
      <h2>{translates?.for('popup.machine_type_select.select_manufacturer_title') ?? 'Select manufacturer'}</h2>
      <ul>
        {manufacturers.map(manu => (
          <li key={manu} className="popup-category-btn"
            onClick={() => setSelectedManufacturer(manu)}>
            {manu}
          </li>
        ))}
      </ul>
    </div>
  }

  // Step 2: Select Category
  if (!selectedCategory) {
    const categories = Array.from(new Set(
      machineTypes
        .filter(mt => mt.manufacturer === selectedManufacturer)
        .map(mt => mt.category)
    )).sort()
    return <div className="machine-type-select-popup">
      <div className='left-btns'>
        <button className="popup-back" onClick={() => setSelectedManufacturer(null)}>
          ← {translates?.for('popup.machine_type_select.back') ?? 'Back'}
        </button>
        {isManager ? <button className="popup-add" onClick={() => addType()}>
          {translates?.for('popup.machine_type_select.add')}
        </button> : null}
      </div>
      <h2>
        {translates?.for('popup.machine_type_select.select_category_title') ?? 'Select category'}
      </h2>
      <ul>
        {categories.map(cat => (
          <li key={cat} className="popup-category-btn"
            onClick={() => setSelectedCategory(cat)}>
            {translates?.for('machine.types.' + cat)}
          </li>
        ))}
      </ul>
    </div>
  }

  // Step 3: Select Model
  const models = machineTypes
    .filter(mt =>
      mt.manufacturer === selectedManufacturer &&
      mt.category === selectedCategory
    )

  return <div className="machine-type-select-popup">
    <div className='left-btns'>
      <button className="popup-back" onClick={() => setSelectedCategory(null)}>
        ← {translates?.for('popup.machine_type_select.back') ?? 'Back'}
      </button>
      {isManager ? <button className="popup-add" onClick={() => addType()}>
        {translates?.for('popup.machine_type_select.add')}
      </button> : null}
    </div>
    <h2>
      {selectedManufacturer} - {translates?.for('machine.types.' + selectedCategory)}
    </h2>
    <ul>
      {models.map(mt => (
        <li key={mt.id}
          className={`popup-item-btn ${selectedModel === mt.model ? 'selected' : ''}`}
          onClick={() => {
            if (!mt) return
            onSelect(mt)
            onClose()
          }}>
          {mt.model}
        </li>
      ))}
    </ul>
  </div>
}